// ChannelToolbar.jsx

import React from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Constants
import {
  isConversationChannel,
  isAiConversation,
  isTextChannel,
  isVoiceVideoChannel,
} from 'channels/constants/channelTypes';

// CSS
import './ChannelToolbar.css';

// Icons
import Icon from 'svg/Icon';

// Components
import Button from 'components/button/Button';
import ChannelNotificationBell from 'channels/components/ChannelNotificationBell';

/**
 * ChannelToolbar
 *
 * Displays selected channel context metadata.
 *
 * @param {object} channel - Selected channel row.
 * @param {string} displayTitle - Optional override for the visible title.
 * @param {number} memberCount - Active member count.
 * @param {boolean} canManageChannel - Whether settings can be edited.
 * @param {boolean} canDeleteChannel - Whether channel can be deleted.
 * @param {boolean} canClearConversation - Whether conversation messages can be cleared.
 * @param {boolean} isClearingConversation - Whether clear is in progress.
 * @param {boolean} clearConversationEnabled - Whether clear is allowed right now.
 * @param {boolean} showNewChatButton - Whether to show New chat (Ai hub).
 * @param {boolean} showMembersButton - Whether to show the members action.
 * @param {boolean} [hideIdentity=false] - Hide name/icon (e.g. GlobalToolbar).
 * @param {string} [actionsPlacement='header'] - 'header' | 'footer' | 'split'.
 * @param {string} [notificationMode] - TEXT channel notification preset.
 * @param {boolean} [notificationModeBusy=false] - Whether preset save is in flight.
 * @param {function} [onNotificationModeChange] - Called with next preset id for TEXT channels.
 * @param {function} onMembersClick - Members click handler.
 * @param {function} onSessionsClick - Sessions / history click handler.
 * @param {function} onNewChatClick - New chat click handler.
 * @param {function} onSettingsClick - Settings click handler.
 * @param {function} onDeleteClick - Delete click handler.
 * @param {function} onClearClick - Clear click handler.
 *
 * @returns {ReactNode} The rendered toolbar.
 */
const ChannelToolbar = React.memo(({
  channel,
  displayTitle,
  memberCount,
  canManageChannel,
  canDeleteChannel,
  canClearConversation,
  isClearingConversation = false,
  clearConversationEnabled = true,
  showNewChatButton = false,
  showMembersButton,
  showSessionsButton = false,
  sessionsPanelCollapsed = true,
  hideIdentity = false,
  actionsPlacement = 'header',
  notificationMode,
  notificationModeBusy = false,
  onNotificationModeChange,
  onMembersClick,
  onSessionsClick,
  onNewChatClick,
  onSettingsClick,
  onDeleteClick,
  onClearClick,
}) => {

  // ----- CONTEXT ----- //

  // Theme Context

  const { theme } = useTheme();

  // ----- DEVICE DETECTION ----- //

  const isMobile = useIsMobile();

  const isConversation = isConversationChannel(channel?.channelType);
  const isAi = isAiConversation(channel);
  const showNotificationBell = isTextChannel(channel?.channelType)
    && typeof onNotificationModeChange === 'function';
  const visibleTitle = displayTitle || channel?.title || (isConversation ? 'Conversation' : 'channel');
  const toolbarIconKey = isConversation
    ? isAi ? 'network_node' : 'chat'
    : (isVoiceVideoChannel(channel?.channelType) ? 'video_camera_front' : 'tag');

  const showIdentity = !hideIdentity;
  const showPrimaryInHeader = actionsPlacement === 'header';
  const showPrimaryInFooter = actionsPlacement === 'footer';
  const showCompactInHeader = actionsPlacement === 'header' || actionsPlacement === 'split';

  const primaryActions = (
    <>
      {showNotificationBell ? (
        <ChannelNotificationBell
          mode={notificationMode}
          busy={notificationModeBusy}
          onModeChange={onNotificationModeChange}
        />
      ) : null}

      {!isConversation && canManageChannel ? (
        <Button
          iconKey="settings"
          iconColor={theme.foregroundMuted}
          iconSize={20}
          height="36px"
          width="36px"
          padding="0"
          backgroundColor="transparent"
          hoverBackgroundColor={theme.buttonHoverBackgroundColor}
          hoverForegroundColor={theme.foreground}
          borderRadius="8px"
          enabled
          onClick={onSettingsClick}
        />
      ) : null}

      {showNewChatButton ? (
        <Button
          iconKey="add"
          label="New Session"
          onClick={onNewChatClick}
        />
      ) : null}

      {canClearConversation ? (
        <Button
          iconKey="ink_eraser"
          label={isClearingConversation ? 'Clearing...' : 'Clear'}
          enabled={!isClearingConversation && clearConversationEnabled}
          onClick={onClearClick}
        />
      ) : null}

      {/* Delete for conversations only — channel delete lives in Channel Settings */}
      {canDeleteChannel && isConversation ? (
        <Button
          iconKey="delete"
          iconColor={theme.foreground}
          iconSize={20}
          height="34px"
          width="34px"
          backgroundColor="transparent"
          hoverBackgroundColor={theme.buttonHoverBackgroundColor}
          hoverIconColor={theme.foreground}
          borderRadius="8px"
          onClick={onDeleteClick}
        />
      ) : null}
    </>
  );

  const showToolbarDelete = Boolean(canDeleteChannel && isConversation);

  if (showPrimaryInFooter) {
    const hasPrimary = (
      showNotificationBell
      || (!isConversation && canManageChannel)
      || showNewChatButton
      || canClearConversation
      || showToolbarDelete
    );
    if (!hasPrimary) return null;

    return primaryActions;
  }

  const hasCompact = (
    (!isMobile && !isAi && memberCount > 0)
    || showSessionsButton
    || (showMembersButton && !isAi)
  );
  const hasPrimaryHeader = showPrimaryInHeader && (
    showNotificationBell
    || (!isConversation && canManageChannel)
    || showNewChatButton
    || canClearConversation
    || showToolbarDelete
  );

  if (!showIdentity && !hasCompact && !hasPrimaryHeader) {
    return null;
  }

  // ----- RENDER ----- //

  return (

    <header
      className="channel-toolbar-container"
    >

      {/* MAIN */}
      {showIdentity ? (
        <div className="channel-toolbar-main">

          {/* NAME */}
          <div
            className="channel-toolbar-name"
          >
            {!isMobile && (
              <Icon
                iconKey={toolbarIconKey}
                width={22}
                height={22}
                color={theme.foreground}
              />
            )}
            <span className="channel-toolbar-name-text">
              {visibleTitle}
            </span>
          </div>

          {/* TOPIC */}
          {!isMobile && !isConversation && channel?.topic ? (

            <div
              className="channel-toolbar-topic"
            >
              {channel.topic}
            </div>
          ) : null}

        </div>
      ) : (
        <div className="channel-toolbar-main" />
      )}

      {/* ACTIONS */}
      {(showCompactInHeader || showPrimaryInHeader) ? (
        <div className="channel-toolbar-actions">

          {showCompactInHeader && !isMobile && !isAi && memberCount > 0 && (
            <>

              {/* META */}
              <div
                className="channel-toolbar-meta"
              >
                {memberCount} member{memberCount === 1 ? '' : 's'}
              </div>
            </>
          )}

          {showCompactInHeader && showSessionsButton ? (
            <Button
              iconKey="history"
              title={sessionsPanelCollapsed ? 'Show sessions' : 'Hide sessions'}
              iconColor={theme.foreground}
              iconSize={20}
              height="34px"
              width="34px"
              backgroundColor="transparent"
              hoverBackgroundColor={theme.buttonHoverBackgroundColor}
              hoverIconColor={theme.foreground}
              borderRadius="8px"
              onClick={onSessionsClick}
            />
          ) : null}

          {showCompactInHeader && showMembersButton && !isAi ? (
            <Button
              iconKey="group"
              iconColor={theme.foreground}
              height="34px"
              width="34px"
              backgroundColor="transparent"
              hoverBackgroundColor={theme.buttonHoverBackgroundColor}
              hoverIconColor={theme.foreground}
              borderRadius="8px"
              onClick={onMembersClick}
            />
          ) : null}

          {showPrimaryInHeader ? primaryActions : null}
        </div>
      ) : null}
    </header>
  );
});

// Dev Tools
ChannelToolbar.displayName = 'ChannelToolbar';

export default ChannelToolbar;
